import { useState, useEffect, useMemo, useCallback } from "react";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import apiService, { BaseURL } from "../../service/Api";
import { useSearchParams } from "react-router-dom";
import InsightStatsCards, { buildInsightStats } from "./components/InsightStatsCards";
import FundsTable from "./components/FundsTable";
import type { Fund } from "./components/FundsTable";
import VerificationQueue from "./components/verification/VerificationQueue";
import { listIdentityVerifications } from "./components/verification/identityVerificationApi";

const TEAL = "#079889";

interface CampaignResponse {
  id: number;
  title: string;
  amount_raised: string;
  donor_count: number;
  status: string;
  currency: string;
  stream: number | null;
  booking: number | null;
  deceased: number | null;
  created_at: string;
}

const PUBLISHED = "published";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface DonationApiItem {
  id: number;
  type_of_donation_name?: string | null;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}/${month}/${day}`;
  } catch {
    return dateString;
  }
};

const toRelativeUrl = (fullUrl: string): string => {
  if (fullUrl.startsWith(BaseURL)) {
    return fullUrl.slice(BaseURL.length);
  }
  try {
    const url = new URL(fullUrl);
    return url.pathname + url.search;
  } catch {
    return fullUrl;
  }
};

async function fetchAllPages<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean>,
): Promise<T[]> {
  const all: T[] = [];
  let nextUrl: string | null = endpoint;
  let isFirstPage = true;

  while (nextUrl) {
    const res: { data: PaginatedResponse<T> } = await apiService.get(
      nextUrl,
      isFirstPage ? params : undefined,
    );
    const data: PaginatedResponse<T> = res.data;
    if (Array.isArray(data)) {
      all.push(...(data as unknown as T[]));
      break;
    }
    all.push(...(data.results || []));
    nextUrl = data.next ? toRelativeUrl(data.next) : null;
    isFirstPage = false;
  }

  return all;
}

const Insights = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get("tab") === "verification" ? 1 : 0;
  });
  const [pendingVerifications, setPendingVerifications] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Fund[]>([]);
  const [allDonations, setAllDonations] = useState<DonationApiItem[]>([]);
  const [togglingCampaignId, setTogglingCampaignId] = useState<number | null>(null);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "verification") {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [searchParams]);

  // Fetch pending count on mount so the badge shows before the tab is visited
  useEffect(() => {
    listIdentityVerifications("pending")
      .then((records) => setPendingVerifications(records.length))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [campaigns, donations] = await Promise.all([
        fetchAllPages<CampaignResponse>("/api/chema/campaigns/"),
        fetchAllPages<DonationApiItem>("/api/chema/donations/"),
      ]);

      setAllDonations(donations);

      const fundsData: Fund[] = campaigns
        .map((c: CampaignResponse) => {
          const total = parseFloat(c.amount_raised || "0");
          return {
            date: formatDate(c.created_at),
            fundName: c.title,
            totalRaised: total,
            totalRaisedDisplay: `${c.currency === "ZWL" ? "ZWL " : "$"}${total.toFixed(2)}`,
            published: c.status === PUBLISHED,
            status: c.status,
            campaignId: c.id,
            streamId: c.stream,
            donationCount: c.donor_count || 0,
          };
        })
        .sort((a: Fund, b: Fund) => b.totalRaised - a.totalRaised);

      setRows(fundsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleStatus = async (fund: Fund) => {
    const newStatus = fund.published ? "paused" : PUBLISHED;
    setTogglingCampaignId(fund.campaignId);
    try {
      await apiService.patch(`/api/chema/campaigns/${fund.campaignId}/`, {
        status: newStatus,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.campaignId === fund.campaignId
            ? { ...r, published: newStatus === PUBLISHED, status: newStatus }
            : r,
        ),
      );
    } catch (error) {
      console.error("Error toggling campaign status:", error);
    } finally {
      setTogglingCampaignId(null);
    }
  };

  const stats = useMemo(
    () => buildInsightStats(rows, allDonations),
    [rows, allDonations],
  );

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <InsightStatsCards stats={stats} />

      {/* Tab bar */}
      <Box sx={{ px: 3, pt: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setSearchParams(v === 1 ? { tab: "verification" } : {});
          }}
          sx={{
            mb: 2,
            "& .MuiTabs-indicator": { bgcolor: TEAL },
            "& .Mui-selected": { color: `${TEAL} !important` },
          }}
        >
          <Tab
            label={
              <Typography variant="body2" fontWeight={600}>
                Campaigns
              </Typography>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Document Verification
                </Typography>
                {pendingVerifications > 0 && (
                  <Box
                    sx={{
                      bgcolor: TEAL,
                      color: "#fff",
                      borderRadius: "999px",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 0.6,
                    }}
                  >
                    {pendingVerifications}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      <Box sx={{ px: 3, pb: 4 }}>
        {activeTab === 0 && (
          <FundsTable
            rows={rows}
            togglingCampaignId={togglingCampaignId}
            onToggleStatus={handleToggleStatus}
          />
        )}
        {activeTab === 1 && <VerificationQueue onPendingCount={setPendingVerifications} />}
      </Box>
    </Box>
  );
};

export default Insights;
